"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";
import { CollectionItem } from "@/@types";
import Collection from "../Collection";
import styles from "./Collections.module.scss";

interface Props {
  data?: { collections?: CollectionItem[] };
  /**
   * Quando todos os cards cabem, esticam para ocupar a largura toda
   * (mantendo 9:16). Com mais itens do que cabem, segue como carrossel.
   */
  fullWidth?: boolean;
}

export default function Collections({ data, fullWidth = false }: Props) {
  const collections = data?.collections ?? [];

  const trackRef = useRef<HTMLUListElement>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  // Só um vídeo com som por vez
  const [playingId, setPlayingId] = useState<number | null>(null);

  const updateScrollState = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    setCanScrollPrev(track.scrollLeft > 1);
    setCanScrollNext(
      track.scrollLeft + track.clientWidth < track.scrollWidth - 1
    );
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    updateScrollState();
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(track);
    return () => observer.disconnect();
  }, [updateScrollState, collections.length]);

  const scroll = (direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    track.scrollBy({
      left: direction * track.clientWidth * 0.8,
      behavior: reduceMotion ? "auto" : "smooth",
    });
  };

  if (collections.length === 0) return null;

  return (
    <div
      className={classNames(styles.carousel, {
        [styles.fullWidth]: fullWidth,
      })}
      role="region"
      aria-label="Inspire-se"
    >
      <ul
        ref={trackRef}
        className={styles.track}
        onScroll={updateScrollState}
      >
        {collections.map((collection) => (
          <li key={collection.id} className={styles.item}>
            <Collection
              collection={collection}
              isPlaying={playingId === collection.id}
              onPlay={() => setPlayingId(collection.id)}
              onStop={() =>
                setPlayingId((id) => (id === collection.id ? null : id))
              }
            />
          </li>
        ))}
      </ul>

      <button
        type="button"
        className={`${styles.arrow} ${styles.arrowPrev}`}
        onClick={() => scroll(-1)}
        disabled={!canScrollPrev}
        aria-label="Anterior"
      >
        <BiChevronLeft />
      </button>
      <button
        type="button"
        className={`${styles.arrow} ${styles.arrowNext}`}
        onClick={() => scroll(1)}
        disabled={!canScrollNext}
        aria-label="Próximo"
      >
        <BiChevronRight />
      </button>
    </div>
  );
}
